import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { api, type Params } from '../api/client';
import type { Paged, Row } from '../api/types';

export interface ListState {
  q: string;
  setQ: (v: string) => void;
  page: number;
  setPage: (p: number) => void;
  sort: string | undefined;
  dir: 'asc' | 'desc' | undefined;
  toggleSort: (key: string) => void;
  filters: Params;
  setFilter: (key: string, value: string | undefined) => void;
  pageSize: number;
}

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

/**
 * Server-side list state (search, sort, paging, filters) + the react-query fetch for it.
 * `endpoint` is an API path like '/agents'. Changing search or filters returns to page 1.
 */
export function useListQuery(endpoint: string, opts: { pageSize?: number; initialFilters?: Params; initialSort?: { key: string; dir: 'asc' | 'desc' } } = {}) {
  const pageSize = opts.pageSize ?? 20;
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<string | undefined>(opts.initialSort?.key);
  const [dir, setDir] = useState<'asc' | 'desc' | undefined>(opts.initialSort?.dir);
  const [filters, setFilters] = useState<Params>(opts.initialFilters ?? {});
  const debouncedQ = useDebounced(q, 300);

  useEffect(() => setPage(1), [debouncedQ, filters]);

  const params = useMemo<Params>(
    () => ({ ...filters, q: debouncedQ || undefined, page, pageSize, sort, dir }),
    [filters, debouncedQ, page, pageSize, sort, dir],
  );

  const query = useQuery({
    queryKey: ['list', endpoint, params],
    queryFn: () => api.get<Paged<Row>>(endpoint, params),
    placeholderData: keepPreviousData,
  });

  const state: ListState = {
    q,
    setQ,
    page,
    setPage,
    sort,
    dir,
    toggleSort: (key) => {
      if (sort === key) setDir((d) => (d === 'asc' ? 'desc' : 'asc'));
      else {
        setSort(key);
        setDir('asc');
      }
    },
    filters,
    setFilter: (key, value) => setFilters((f) => ({ ...f, [key]: value || undefined })),
    pageSize,
  };
  return { query, state };
}
